import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["unitPrice", "quantity", "taxRate", "salesTaxApplicable", "valueExclSt", "finalValue", "extraTax", "furtherTax", "discount", "retailPrice", "product", "uom", "description"]

  connect() {
    this.timeout = null
    this.abortController = null
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  productChanged() {
    const url = this.hasProductTarget ? this.productTarget.selectedOptions[0]?.dataset.url : ""
    if (!url) return

    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()

    fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: this.abortController.signal
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product) return
        this.populateFromProduct(product)
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Failed to load product details", error)
        }
      })
  }

  populateFromProduct(product) {
    if (product.uom && this.hasUomTarget) {
      const uomSelect = this.uomTarget
      const hasOption = Array.from(uomSelect.options).some((option) => option.value === product.uom)
      if (!hasOption) {
        uomSelect.add(new Option(product.uom, product.uom))
      }
      uomSelect.value = product.uom
    }

    if (product.description && this.hasDescriptionTarget) {
      this.descriptionTarget.value = product.description
    }

    if (product.price != null && product.price !== "" && this.hasUnitPriceTarget) {
      this.unitPriceTarget.value = this.formatDecimal(product.price)
      this.calculate()
    }
  }

  calculate() {
    // Debounce: clear previous timeout and set a new one
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    this.timeout = setTimeout(() => {
      this.performCalculation()
    }, 300)
  }

  performCalculation() {
    let unitPrice = parseFloat(this.unitPriceTarget.value) || 0
    const quantity = parseFloat(this.quantityTarget.value) || 0
    const taxRate = parseFloat(this.taxRateTarget.value) || 0

    const extraTax = parseFloat(this.extraTaxTarget.value) || 0
    const furtherTax = parseFloat(this.furtherTaxTarget.value) || 0
    const discount = parseFloat(this.discountTarget.value) || 0

    const retailPrice = parseFloat(this.retailPriceTarget.value) || 0

    if (retailPrice && retailPrice > 0) {
      unitPrice = retailPrice
    }
    // Calculate Value (Excl. ST) = quantity * unit_price
    const valueExclSt = quantity * unitPrice

    // Calculate ST Applicable = (Value Excl. ST) * (tax_rate / 100)
    const salesTaxApplicable = valueExclSt * (taxRate / 100)

    // Calculate Final Value = Value (Excl. ST) + ST Applicable
    const finalValue = valueExclSt + salesTaxApplicable + extraTax + furtherTax - discount

    // Update disabled fields
    this.salesTaxApplicableTarget.value = this.formatDecimal(salesTaxApplicable)
    this.finalValueTarget.value = this.formatDecimal(finalValue)
    this.valueExclStTarget.value = this.formatDecimal(valueExclSt)
  }

  formatDecimal(value) {
    const rounded = Math.round((Number(value) || 0) * 100) / 100
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2)
  }
}
